import type {
  FeedbackCategory,
  FeedbackDisplayStatus,
  FeedbackImpact,
  FeedbackStatus,
  ProductFeedbackType,
} from '@doneverse/shared'
import { Bug, Lightbulb, MessageCircle, type LucideIcon } from 'lucide-react'
import type { BadgeVariant } from '@/components/ui/badge'

/**
 * Presentation for the Feedback Loop, shared by the submitter surface and the
 * admin queue so the two can never label the same item differently.
 */

interface TypeMeta {
  label: string
  icon: LucideIcon
  variant: BadgeVariant
}

export const FEEDBACK_TYPE_META: Record<ProductFeedbackType, TypeMeta> = {
  bug_report: { label: 'Bug', icon: Bug, variant: 'red' },
  feature_request: { label: 'Feature', icon: Lightbulb, variant: 'purple' },
  general: { label: 'General', icon: MessageCircle, variant: 'blue' },
}

/**
 * The submitter's five statuses. `escalated` never appears — it collapses into
 * "In Progress" server-side, because a submitter should not learn whether a
 * ClickUp ticket exists.
 */
export const FEEDBACK_DISPLAY_STATUS_VARIANT: Record<FeedbackDisplayStatus, BadgeVariant> = {
  Submitted: 'gray',
  Received: 'blue',
  'In Progress': 'amber',
  Logged: 'purple',
  Resolved: 'green',
}

export const FEEDBACK_STATUS_META: Record<FeedbackStatus, { label: string; variant: BadgeVariant }> = {
  new: { label: 'New', variant: 'red' },
  acknowledged: { label: 'Acknowledged', variant: 'blue' },
  in_progress: { label: 'In Progress', variant: 'amber' },
  escalated: { label: 'Escalated', variant: 'orange' },
  closed: { label: 'Closed', variant: 'gray' },
}

export const FEEDBACK_CATEGORY_META: Record<FeedbackCategory, { label: string }> = {
  bug: { label: 'Bug' },
  feature_request: { label: 'Feature Request' },
  general_feedback: { label: 'General Feedback' },
  user_error: { label: 'User Error' },
}

/**
 * Severity for bugs, importance for feature requests. Self-reported and not
 * binding — everyone's bug is High — so it reads as signal, never instruction.
 */
export const FEEDBACK_IMPACT_META: Record<FeedbackImpact, { label: string; variant: BadgeVariant }> = {
  low: { label: 'Low', variant: 'gray' },
  medium: { label: 'Medium', variant: 'amber' },
  high: { label: 'High', variant: 'red' },
  nice_to_have: { label: 'Nice to have', variant: 'gray' },
  would_really_help: { label: 'Would really help', variant: 'amber' },
  game_changer: { label: 'Game changer', variant: 'purple' },
}

/** Impact options per form — general feedback captures none. */
export const FEEDBACK_IMPACT_OPTIONS: Record<ProductFeedbackType, FeedbackImpact[]> = {
  bug_report: ['low', 'medium', 'high'],
  feature_request: ['nice_to_have', 'would_really_help', 'game_changer'],
  general: [],
}

/**
 * What titles an item in a list. Rows submitted through the old single-textarea
 * widget have no label, so fall back to their description.
 */
export function feedbackTitle(item: { label: string | null; description: string }): string {
  const label = item.label?.trim()
  if (label) return label
  const description = item.description.trim()
  return description.length > 140 ? `${description.slice(0, 137)}…` : description
}
